import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { LuBanknote, LuPencil, LuPlus, LuTrash2 } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { simpleBreakdown } from '../salaryMath';
import AddSalaryModal from './AddSalaryModal';
import useEmployeeFinanceText from './employeeFinanceText';

// Tablo sütunları (eski tablonun sırası); değerler brüt ücretten hesaplanır
const AMOUNT_COLUMNS = [
  { key: 'grossSalary', tone: 'positive' },
  { key: 'sskEmployeeShare', tone: 'negative' },
  { key: 'unemploymentEmployeeShare', tone: 'negative' },
  { key: 'stampTax', tone: 'negative' },
  { key: 'totalDeductions', tone: 'negative' },
  { key: 'netSalary', tone: 'positive' },
  { key: 'sskEmployerShare' },
  { key: 'unemploymentEmployerShare' },
  { key: 'totalEmployerCost', strong: true },
];
const TONE_CLASS = {
  positive: 'tw-text-emerald-700 dark:tw-text-emerald-300',
  negative: 'tw-text-rose-600 dark:tw-text-rose-300',
};
const TH = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-text-slate-400';
const TD = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-sm tw-tabular-nums';

/**
 * Salary records of the selected employee (Salaries), newest first, with
 * the deductions and employer cost; add, edit and delete (asked first).
 */
const SalaryModal = ({ setSalaries: setParentSalaries, selectedEmployee, onClose }) => {
  const { language, label, formatMoney, formatDate } = useEmployeeFinanceText();
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ open: false, salary: null, key: 0 });
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const updateSalaries = useCallback((list) => {
    setSalaries(list);
    setParentSalaries?.(list);
  }, [setParentSalaries]);

  const fetchEmployeeSalaries = useCallback(async () => {
    if (!selectedEmployee || !selectedEmployee.id) return;
    try {
      setLoading(true);
      const salaryQuery = query(collection(db, 'Salaries'), where('employeeId', '==', selectedEmployee.id));
      const querySnapshot = await getDocs(salaryQuery);
      const salaryData = querySnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));

      // Sort by timestamp (createdAt or updatedAt), most recent first
      salaryData.sort((a, b) => {
        const timeA = a.updatedAt || a.createdAt || 0;
        const timeB = b.updatedAt || b.createdAt || 0;
        return timeB - timeA;
      });

      updateSalaries(salaryData);
    } catch (error) {
      console.error('Error fetching salary data:', error);
      toast.error(label('salaries.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee, updateSalaries, label]);

  useEffect(() => {
    fetchEmployeeSalaries();
  }, [fetchEmployeeSalaries]);

  const openForm = (salary = null) => setForm((current) => ({ open: true, salary, key: current.key + 1 }));
  const closeForm = () => setForm((current) => ({ ...current, open: false }));

  const handleSalaryAdded = (newSalary) => updateSalaries([newSalary, ...salaries]);
  const handleSalaryUpdated = (updatedSalary) => updateSalaries(salaries.map((salary) => (salary.id === updatedSalary.id ? updatedSalary : salary)));

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'Salaries', toDelete.id));
      updateSalaries(salaries.filter((salary) => salary.id !== toDelete.id));
      toast.success(label('salaries.deleted'));
      setToDelete(null);
    } catch (error) {
      console.error('Error deleting salary:', error);
      toast.error(label('salaries.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const employeeName = `${selectedEmployee?.eName || ''} ${selectedEmployee?.eSurname || ''}`.trim();
  const rows = salaries.map((salary) => ({ salary, values: simpleBreakdown(salary.grossSalary) }));

  const rowActions = (salary) => (
    <span className="tw-flex tw-items-center tw-gap-1">
      <button type="button" onClick={() => openForm(salary)} title={label('salaries.edit')} aria-label={label('salaries.edit')} className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-text-blue-600 hover:tw-bg-blue-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-500/40 dark:tw-text-blue-300">
        <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
      </button>
      <button type="button" onClick={() => setToDelete(salary)} title={label('salaries.delete')} aria-label={label('salaries.delete')} className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-text-rose-600 hover:tw-bg-rose-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-500/40 dark:tw-text-rose-300">
        <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
      </button>
    </span>
  );

  let body;
  if (loading) {
    body = <p className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('salaries.loading')}</p>;
  } else if (rows.length === 0) {
    body = (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-10 tw-text-center">
        <LuBanknote aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-slate-300 dark:tw-text-slate-600" />
        <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('salaries.empty')}</p>
      </div>
    );
  } else {
    body = (
      <>
        {/* Phones: one card per record */}
        <ul className="tw-space-y-2.5 md:tw-hidden">
          {rows.map(({ salary, values }, index) => (
            <li key={salary.id} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <div className="tw-min-w-0">
                  <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
                    {formatDate(salary.contractDate)}
                    {index === 0 && <Badge tone="accent">{label('salaries.current')}</Badge>}
                  </p>
                  <p className="tw-mt-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('salaries.columns.netSalary')}</p>
                  <p className="tw-text-lg tw-font-semibold tw-tabular-nums tw-text-emerald-700 dark:tw-text-emerald-300">{formatMoney(values.netSalary)}</p>
                </div>
                {rowActions(salary)}
              </div>
              <dl className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-xs">
                {['grossSalary', 'totalDeductions', 'totalEmployerCost'].map((key) => (
                  <div key={key} className="tw-min-w-0">
                    <dt className="tw-truncate tw-text-slate-500 dark:tw-text-slate-400">{label(`salaries.columns.${key}`)}</dt>
                    <dd className="tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{formatMoney(values[key])}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>

        {/* Wider screens: the full table (first column stays in view) */}
        <div className="tw-hidden tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 md:tw-block dark:tw-border-white/10 glass-scrollbar">
          <table className="tw-w-full tw-border-collapse">
            <thead className="tw-bg-slate-50/80 dark:tw-bg-white/[0.03]">
              <tr>
                <th className={`${TH} tw-sticky tw-left-0 tw-z-[1] tw-bg-slate-50 dark:tw-bg-[#262a33]`}>{label('salaries.columns.contractDate')}</th>
                {AMOUNT_COLUMNS.map((column) => <th key={column.key} className={`${TH} tw-text-right`}>{label(`salaries.columns.${column.key}`)}</th>)}
                <th className={TH}><span className="tw-sr-only">{label('salaries.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ salary, values }, index) => (
                <tr key={salary.id} className="tw-border-t tw-border-slate-200/70 hover:tw-bg-slate-50/60 dark:tw-border-white/[0.06] dark:hover:tw-bg-white/[0.02]">
                  <td className={`${TD} tw-sticky tw-left-0 tw-z-[1] tw-bg-white tw-font-semibold tw-text-slate-900 dark:tw-bg-[#22262f] dark:tw-text-white`}>
                    <span className="tw-flex tw-items-center tw-gap-2">
                      {formatDate(salary.contractDate)}
                      {index === 0 && <Badge tone="accent">{label('salaries.current')}</Badge>}
                    </span>
                  </td>
                  {AMOUNT_COLUMNS.map((column) => (
                    <td key={column.key} className={`${TD} tw-text-right ${TONE_CLASS[column.tone] || 'tw-text-slate-700 dark:tw-text-slate-200'} ${column.strong ? 'tw-font-semibold' : ''}`}>
                      {formatMoney(values[column.key])}
                    </td>
                  ))}
                  <td className={`${TD} tw-w-px`}>{rowActions(salary)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        title={label('salaries.title')}
        subtitle={employeeName}
        closeLabel={label('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
            <LuBanknote aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={(
          <Button variant="primary" size="sm" onClick={() => openForm(null)}>
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            <span className="tw-hidden sm:tw-inline">{label('salaries.add')}</span>
          </Button>
        )}
        footer={<Button variant="ghost" onClick={onClose}>{label('common.close')}</Button>}
      >
        <div className="tw-space-y-3">
          <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('salaries.note')}</p>
          {body}
        </div>
      </Modal>

      {form.open && selectedEmployee && (
        <AddSalaryModal
          key={form.key}
          onClose={closeForm}
          onSalaryAdded={handleSalaryAdded}
          onSalaryUpdated={handleSalaryUpdated}
          employeeId={selectedEmployee.id}
          editingSalary={form.salary}
          employeeName={employeeName}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        title={label('salaries.deleteTitle')}
        message={label('salaries.deleteMessage', { date: toDelete ? formatDate(toDelete.contractDate) : '' })}
        confirmLabel={label('salaries.delete')}
        cancelLabel={label('common.cancel')}
        tone="danger"
        icon={LuTrash2}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setToDelete(null)}
      />
    </>
  );
};

SalaryModal.propTypes = {
  setSalaries: PropTypes.func,
  selectedEmployee: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default SalaryModal;
